import { Inject, Injectable } from "@angular/core";
import { IS_BROWSER } from "../utils/injectiontokens";

/**
 * Service for safely interacting with localStorage
 * Handles exceptions and provides fallbacks when localStorage isn't available
 */
@Injectable({
  providedIn: 'root'
})
export class LocalStorageService {
  
  constructor(@Inject(IS_BROWSER) protected isBrowser: boolean) {}
  
  /**
   * Checks if localStorage is available and accessible
   */
  private isAvailable(): boolean {
    try {
      if (!this.isBrowser) return false;
      
      // Test if we can access localStorage
      const test = '__test__';
      localStorage.setItem(test, test);
      localStorage.removeItem(test);
      return true;
    } catch (e) {
      return false;
    }
  }
  
  /**
   * Safely gets an item from localStorage
   * @param key The key to retrieve
   * @returns The value or null if not found or not available
   */
  public getItem(key: string): string | null {
    if (this.isAvailable()) {
      try {
        return localStorage.getItem(key);
      } catch (e) {
        console.warn(`Error accessing localStorage for key ${key}:`, e);
      }
    }
    return null;
  }
  
  /**
   * Safely gets an item from localStorage and parses it as JSON
   * @param key The key to retrieve
   * @returns The parsed object or null if not found or invalid
   */
  public getObject<T>(key: string): T | null {
    const value = this.getItem(key);
    if (value) {
      try {
        return JSON.parse(value) as T;
      } catch (e) {
        console.warn(`Error parsing JSON from localStorage for key ${key}:`, e);
      }
    }
    return null;
  }
  
  /**
   * Safely sets an item in localStorage
   * @param key The key to set
   * @param value The value to store
   */
  public setItem(key: string, value: string): void {
    if (this.isAvailable()) {
      try {
        localStorage.setItem(key, value);
      } catch (e) {
        console.warn(`Error setting localStorage for key ${key}:`, e);
      }
    }
  }
  
  /**
   * Safely stores an object in localStorage by converting it to JSON
   * @param key The key to set
   * @param value The object to store
   */
  public setObject(key: string, value: any): void {
    try {
      const jsonValue = JSON.stringify(value);
      this.setItem(key, jsonValue);
    } catch (e) {
      console.warn(`Error stringifying object for localStorage key ${key}:`, e);
    }
  }
  
  /**
   * Safely removes an item from localStorage
   * @param key The key to remove
   */
  public removeItem(key: string): void {
    if (this.isAvailable()) {
      try {
        localStorage.removeItem(key);
      } catch (e) {
        console.warn(`Error removing item from localStorage for key ${key}:`, e);
      }
    }
  }
  
  /**
   * Safely clears all items from localStorage
   */
  public clear(): void {
    if (this.isAvailable()) {
      try {
        localStorage.clear();
      } catch (e) {
        console.warn('Error clearing localStorage:', e);
      }
    }
  }
} 